import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/*
 * TopBar — DESIGN_SYSTEM.md §2.3
 *
 * 64px-min sticky top bar with three slot zones: leading (greeting,
 * back button), center (search, breadcrumb), trailing (right cluster:
 * language pill, timezone, icon buttons, primary action). All slots
 * are optional.
 *
 *   <TopBar
 *     leading={<TopBar.Greeting title="Good morning, Marcus" sub="Tuesday" />}
 *     trailing={<LanguagePill />}
 *   />
 *
 * Variants are handled by the caller via `variant`:
 *   default → `bg-surface` + bottom border
 *   transparent → no surface (e.g. over hero gradient)
 *   marketing → translucent white with backdrop blur (applies on scroll
 *               via the consumer's own scroll listener)
 *   dark → `bg-ink` + white text (for booking confirmation, etc.)
 */

export type TopBarVariant = 'default' | 'transparent' | 'marketing' | 'dark';

interface TopBarProps {
  leading?: ReactNode;
  center?: ReactNode;
  trailing?: ReactNode;
  variant?: TopBarVariant;
  /** Stick to the top of the viewport (`sticky top-0 z-30`). Default true. */
  sticky?: boolean;
  className?: string;
}

const VARIANT_CLASS: Record<TopBarVariant, string> = {
  default:     'bg-surface border-b border-line',
  transparent: 'bg-transparent',
  marketing:   'bg-surface/80 backdrop-blur-md border-b border-line/50',
  dark:        'bg-ink text-white border-b border-white/10',
};

export function TopBar({
  leading,
  center,
  trailing,
  variant = 'default',
  sticky = true,
  className,
}: TopBarProps) {
  return (
    <header
      className={cn(
        'min-h-[64px]',
        sticky && 'sticky top-0 z-30',
        VARIANT_CLASS[variant],
        className,
      )}
    >
      <div className="mx-auto flex h-16 items-center gap-3 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-1 items-center gap-3 min-w-0">{leading}</div>
        {center && <div className="flex min-w-0 items-center justify-center">{center}</div>}
        <div className="flex md:flex-1 items-center justify-end gap-2 min-w-0">{trailing}</div>
      </div>
    </header>
  );
}

interface GreetingProps {
  title: ReactNode;
  sub?: ReactNode;
  className?: string;
}

function Greeting({ title, sub, className }: GreetingProps) {
  return (
    <div className={cn('min-w-0', className)}>
      <h1 className="text-base sm:text-h2 text-ink truncate">{title}</h1>
      {sub && <p className="text-body-xs sm:text-body-sm text-soft truncate">{sub}</p>}
    </div>
  );
}

interface DividerProps {
  className?: string;
}

function Divider({ className }: DividerProps) {
  return <span className={cn('h-6 w-px bg-line', className)} aria-hidden />;
}

TopBar.Greeting = Greeting;
TopBar.Divider = Divider;
